/**
 * @file EventsSection.tsx
 * @summary Section page d'accueil — EventsSection
 *
 * Bloc de contenu affiché sur la home (`/`). Composant présentationnel utilisant les tokens du design system (Charbon/Or, Bebas Neue/Inter).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { useState, useEffect, useCallback, useRef } from "react";
import Section from "@/components/Section";
import { useInView } from "@/hooks/use-in-view";
import { Trophy, Heart, PartyPopper, Users, Medal, ShoppingBag, Handshake, ChevronLeft, ChevronRight, Play } from "lucide-react";
import photoCompetition1 from "@/assets/photo-competition-1.webp";
import photoNoel from "@/assets/photo-noel.webp";
import photoCalendrier from "@/assets/photo-calendrier.webp";
import photoCompetition2 from "@/assets/photo-competition-2.webp";
import photoCheque from "@/assets/photo-cheque-cancer.webp";
import photoContest1 from "@/assets/photo-contest-1.webp";
import photoContest2 from "@/assets/photo-contest-2.webp";
import photoCompetition5 from "@/assets/photo-competition-5.webp";
import photoAthletes2 from "@/assets/photo-athletes-2.webp";
import photoFamilyContest from "@/assets/photo-family-contest.webp";
import photoBoxEvent from "@/assets/photo-box-event.webp";
import photoTeamCircle from "@/assets/photo-team-circle.webp";
import photoAnniversaire from "@/assets/photo-anniversaire.webp";
import photoGroupe from "@/assets/photo-groupe.webp";
import photoDumbbellComp from "@/assets/photo-dumbbell-competition.webp";
import photoOctobreRose1 from "@/assets/photo-octobre-rose-1.webp";
import photoOctobreRoseAffiche from "@/assets/photo-octobre-rose-affiche.webp";
import photoOctobreRose2 from "@/assets/photo-octobre-rose-2.webp";
import photoCalendrier2 from "@/assets/photo-calendrier-2.webp";
import photoCheque2 from "@/assets/photo-cheque-cancer-2.webp";
import photoCheque3 from "@/assets/photo-cheque-cancer-3.webp";
import photoContestGroupe from "@/assets/photo-contest-groupe.webp";
import photoContestPodium from "@/assets/photo-contest-podium.webp";
import photoFamilyContest2 from "@/assets/photo-family-contest-2.webp";
import photoAffiliatesBattle2 from "@/assets/photo-affiliates-battle-2.webp";
import photoRacletteNoel from "@/assets/photo-raclette-noel.webp";
import photoFamilyContestAffiche from "@/assets/photo-family-contest-affiche.webp";
import photoCalendrierCancer from "@/assets/photo-calendrier-cancer.webp";
import photoTshirts from "@/assets/photo-tshirts.webp";
import photoWeLove from "@/assets/photo-we-love.webp";

const eventsWithPhotos = [
  {
    icon: Trophy,
    title: "Compétitions internes",
    desc: "Chistera Contest, WOD compétitifs entre adultes… Podium, lots et souvenirs garantis ! Un rendez-vous incontournable.",
    photos: [photoContestPodium, photoContest1, photoContest2, photoContestGroupe, photoCompetition5, photoDumbbellComp],
  },
  {
    icon: Users,
    title: "Family Contest",
    desc: "Compétitions parents-enfants (6-10 ans) : l'esprit CrossFit en famille ! Un moment unique de partage et de dépassement.",
    photos: [photoFamilyContest2, photoFamilyContest, photoFamilyContestAffiche],
  },
  {
    icon: Heart,
    title: "Événements caritatifs",
    desc: "Octobre Rose (WOD 100% féminin), calendrier « Esprit CrossFit » au profit de la Ligue contre le Cancer (1 000 € reversés).",
    photos: [photoCheque, photoOctobreRose2, photoOctobreRoseAffiche, photoOctobreRose1, photoCalendrier, photoCalendrierCancer, photoCalendrier2, photoCheque2, photoCheque3],
  },
  {
    icon: PartyPopper,
    title: "WODs thématiques & soirées",
    desc: "Raclette de Noël, WOD galette, Mardi Gras déguisé, « Viens WODer avec ton pote »… On s'entraîne dur, mais on sait s'amuser !",
    photos: [photoRacletteNoel, photoNoel, photoAnniversaire],
  },
  {
    icon: Medal,
    title: "Compétitions externes",
    desc: "Affiliates Battle, Lion's Contest, Winter Games, Open CrossFit, Showdown… Chistera vous accompagne sur les compétitions nationales et régionales.",
    photos: [photoAffiliatesBattle2, photoCompetition2, photoCompetition1, photoAthletes2],
  },
  {
    icon: ShoppingBag,
    title: "Merch Chistera",
    desc: "T-shirts, sweats et accessoires aux couleurs de CrossFit Chistera — portez vos couleurs !",
    photos: [photoTshirts, photoWeLove],
  },
  {
    icon: Handshake,
    title: "Vie de la communauté",
    desc: "Anniversaires, apéros, journées portes ouvertes… Bien plus qu'une salle, une vraie famille.",
    photos: [photoBoxEvent, photoTeamCircle, photoGroupe],
  },
];

function MiniCarousel({ photos, title }: { photos: string[]; title: string }) {
  const [idx, setIdx] = useState(0);
  const { ref, isInView } = useInView(0.1);

  const next = useCallback(() => {
    setIdx((c) => (c + 1) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    if (photos.length <= 1 || !isInView) return;
    const interval = setInterval(next, 3000);
    return () => clearInterval(interval);
  }, [next, photos.length, isInView]);

  if (photos.length === 0) return null;

  return (
    <div ref={ref} className="relative aspect-[4/3] rounded-lg overflow-hidden">
      <img
        src={isInView ? photos[idx] : photos[0]}
        alt={`${title} — CrossFit Chistera (Saint-Pierre-de-Chandieu, Lyon Est)`}
        className="w-full h-full object-cover transition-opacity duration-500"
        loading="lazy"
        decoding="async"
      />
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setIdx((c) => (c - 1 + photos.length) % photos.length); }}
            className="absolute left-1 top-1/2 -translate-y-1/2 bg-charcoal/60 text-white rounded-full p-1 hover:bg-charcoal transition-colors"
            aria-label="Précédent"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-1 top-1/2 -translate-y-1/2 bg-charcoal/60 text-white rounded-full p-1 hover:bg-charcoal transition-colors"
            aria-label="Suivant"
          >
            <ChevronRight size={14} />
          </button>
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
            {photos.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? "bg-gold" : "bg-white/50"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function EventsSection() {
  return (
    <Section id="evenements" className="bg-card">
      <div className="text-center mb-12">
        <span className="inline-block text-sm font-semibold text-gold tracking-wider mb-2 font-body normal-case">Plus qu'un sport, une communauté</span>
        <h2 className="text-4xl md:text-5xl text-foreground">Événements & vie de la box</h2>
      </div>

      {/* Event cards with associated photos */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {eventsWithPhotos.map((e) => (
          <div key={e.title} className="bg-background border border-border rounded-lg overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
            <MiniCarousel photos={e.photos} title={e.title} />
            <div className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                  <e.icon className="text-gold" size={18} />
                </div>
                <h3 className="text-xl text-foreground">{e.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground font-body normal-case leading-relaxed">{e.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Témoignages vidéos */}
      <div className="mt-16">
        <div className="text-center mb-8">
          <h3 className="text-2xl md:text-3xl text-foreground">Ils témoignent</h3>
          <p className="text-muted-foreground font-body normal-case text-sm mt-2">La parole à nos adhérents</p>
        </div>
        <TestimonialCarousel />
      </div>
    </Section>
  );
}

type TestimonialVideo = { src: string; poster: string; title: string; description: string };

const testimonialVideos: TestimonialVideo[] = [
  { src: "/testimonials/temoignage-1.mp4", poster: "/testimonials/temoignage-1.jpg", title: "Témoignage adhérent CrossFit Chistera — n°1", description: "Avis vidéo d'un membre de CrossFit Chistera à Saint-Pierre-de-Chandieu sur son expérience à la box." },
  { src: "/testimonials/temoignage-2.mp4", poster: "/testimonials/temoignage-2.jpg", title: "Témoignage adhérent CrossFit Chistera — n°2", description: "Retour d'expérience d'un crossfitter de la box Chistera dans l'Est lyonnais." },
  { src: "/testimonials/temoignage-3.mp4", poster: "/testimonials/temoignage-3.jpg", title: "Témoignage adhérent CrossFit Chistera — n°3", description: "Avis sur les coachs et l'ambiance de CrossFit Chistera à Saint-Pierre-de-Chandieu." },
  { src: "/testimonials/temoignage-4.mp4", poster: "/testimonials/temoignage-4.jpg", title: "Témoignage adhérent CrossFit Chistera — n°4", description: "Témoignage d'un membre sur sa progression au CrossFit Chistera." },
  { src: "/testimonials/temoignage-5.mp4", poster: "/testimonials/temoignage-5.jpg", title: "Témoignage adhérent CrossFit Chistera — n°5", description: "Avis d'un adhérent de CrossFit Chistera sur la communauté de la box." },
  { src: "/testimonials/temoignage-6.mp4", poster: "/testimonials/temoignage-6.jpg", title: "Témoignage adhérent CrossFit Chistera — n°6", description: "Retour d'expérience d'un débutant accueilli à CrossFit Chistera à Saint-Pierre-de-Chandieu." },
  { src: "/testimonials/temoignage-7.mp4", poster: "/testimonials/temoignage-7.jpg", title: "Témoignage adhérent CrossFit Chistera — n°7", description: "Témoignage sur les cours WOD et l'encadrement à CrossFit Chistera." },
  { src: "/testimonials/temoignage-8.mp4", poster: "/testimonials/temoignage-8.jpg", title: "Témoignage adhérent CrossFit Chistera — n°8", description: "Avis vidéo d'un membre sur l'esprit familial de la box Chistera." },
  { src: "/testimonials/temoignage-9.mp4", poster: "/testimonials/temoignage-9.jpg", title: "Témoignage adhérent CrossFit Chistera — n°9", description: "Témoignage sur les événements et compétitions de CrossFit Chistera." },
  { src: "/testimonials/temoignage-10.mp4", poster: "/testimonials/temoignage-10.jpg", title: "Témoignage adhérent CrossFit Chistera — n°10", description: "Avis d'un crossfitter sur la box CrossFit Chistera dans l'Est lyonnais." },
];

function TestimonialCarousel() {
  const [playingIdx, setPlayingIdx] = useState<number | null>(null);
  const [current, setCurrent] = useState(0);
  const [perView, setPerView] = useState(1);
  const { ref, isInView } = useInView(0.1);

  useEffect(() => {
    const update = () => {
      setPerView(window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % testimonialVideos.length);
    setPlayingIdx(null);
  }, []);

  const prev = () => {
    setCurrent((c) => (c - 1 + testimonialVideos.length) % testimonialVideos.length);
    setPlayingIdx(null);
  };

  useEffect(() => {
    if (playingIdx !== null || !isInView) return;
    const interval = setInterval(next, 6000);
    return () => clearInterval(interval);
  }, [next, playingIdx, isInView]);

  const visible = Array.from({ length: perView }, (_, i) => {
    const idx = (current + i) % testimonialVideos.length;
    return { ...testimonialVideos[idx], idx };
  });

  // SEO: VideoObject JSON-LD for each testimonial
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": testimonialVideos.map((v) => ({
      "@type": "VideoObject",
      name: v.title,
      description: v.description,
      thumbnailUrl: `https://crossfitchistera.fr${v.poster}`,
      contentUrl: `https://crossfitchistera.fr${v.src}`,
      uploadDate: "2026-05-12T10:00:00+02:00",
    })),
  };

  return (
    <div ref={ref} className="max-w-4xl mx-auto relative">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="flex gap-4 justify-center">
        {visible.map((video) => (
          <div key={video.idx} className="flex-1 max-w-[220px] bg-charcoal border border-charcoal rounded-lg overflow-hidden">
            <div className="aspect-[9/16] flex items-center justify-center relative">
              {playingIdx === video.idx ? (
                <video
                  src={video.src}
                  poster={video.poster}
                  title={video.title}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover bg-charcoal"
                  aria-label={video.description}
                />
              ) : (
                <button
                  onClick={() => setPlayingIdx(video.idx)}
                  className="w-full h-full relative group cursor-pointer"
                  aria-label={`Lancer ${video.title}`}
                >
                  <img
                    src={video.poster}
                    alt={video.description}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-charcoal/30 group-hover:bg-charcoal/10 transition-colors flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-gold/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="text-charcoal ml-0.5" size={24} fill="currentColor" />
                    </div>
                  </div>
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Navigation arrows */}
      <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-charcoal/70 text-white rounded-full p-2 hover:bg-charcoal transition-colors" aria-label="Précédent">
        <ChevronLeft size={20} />
      </button>
      <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-charcoal/70 text-white rounded-full p-2 hover:bg-charcoal transition-colors" aria-label="Suivant">
        <ChevronRight size={20} />
      </button>

      {/* Dots */}
      <div className="flex justify-center gap-1.5 mt-4 flex-wrap">
        {testimonialVideos.map((_, i) => (
          <button key={i} onClick={() => { setCurrent(i); setPlayingIdx(null); }} className={`w-2 h-2 rounded-full transition-colors ${i === current ? "bg-gold" : "bg-border"}`} aria-label={`Témoignage ${i + 1}`} />
        ))}
      </div>
    </div>
  );
}